import useMediaAttachment from "@/middleware/hooks/attachments/useMediaAttachment";
import { useMemo } from "react";

import { Mic } from "lucide-react";

import { MessageEntity } from "@/indexedDB/types";

type Props = {
  message: MessageEntity;
};

export default function PinnedMessage({ message }: Props) {
  const data = useMemo(() => {
    return {
      chatId: message?.chatId || "",
      messageId: message?.id ?? "",
      messageTimestamp: message?.timestamp ?? "",
      attachment: {
        attachmentId: message?.content.media?.attachmentId ?? "",
        attachmentThumbnailId: "",
        filename: message?.content.media?.filename ?? "",
        fileType: message?.content.media?.fileType ?? "",
        fileSize: 0,
      },
    };
  }, [message]);

  const { loading, attachmentUrl } = useMediaAttachment({
    ...data,
    getThumbnail: true,
    canExecute:
      (message && message.content.media?.fileType.startsWith("image")) ?? false,
  });

  const textMessage =
    (message?.content.link ? message.content.text : null) ||
    message?.content.text ||
    message?.content.file?.filename ||
    message?.content.media?.filename ||
    "";

  // TO DO -> improve content extraction (handle spoilers) => global util?
  // Replace spoiler tags and their content with "******"
  const plainTextContent = textMessage
    .replace(/<spoiler\b[^>]*>.*?<\/spoiler>/gi, "******")
    // Remove all remaining HTML tags
    .replace(/<[^>]*>?/gm, "")
    .trim();

  const isImage = attachmentUrl !== "";
  const isVoiceMessage = !!message.content.voice;

  return (
    <>
      {isImage && (
        <div className="relative ml-2 size-24 flex-none border border-primary bg-muted">
          {!loading && (
            <img
              className="size-full object-cover"
              src={attachmentUrl}
              alt={message?.content.media?.filename || "Pinned message image"}
            />
          )}
        </div>
      )}

      {isVoiceMessage && (
        <div className="mx-2 flex items-center gap-1 align-middle">
          <Mic size={16} className="text-secondary" />
          <span>Voice Message</span>
        </div>
      )}

      <p className="mx-2 line-clamp-3 min-w-0 flex-1 overflow-hidden break-words opacity-80">
        {plainTextContent}
      </p>
    </>
  );
}
